import { useEffect, useState } from "react";
import { Hourglass } from "lucide-react";
import { api } from "@/lib/api";
import { HAZARD_LABEL } from "@/lib/hazard";

const left = (iso, now) => {
  const ms = new Date(iso) - now;
  if (ms <= 0) return "due now";
  const h = Math.floor(ms / 3.6e6), m = Math.floor((ms % 3.6e6) / 6e4);
  return h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${m}m`;
};

const missCheck = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const mon = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `${mon} ${d.getUTCDate()} ${d.toISOString().slice(11, 16)} UTC`;
};

export const ScoringClock = ({ open }) => {
  const [s, setS] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!open) return;
    const load = () => api.predictionSchedule().then(setS).catch(() => {});
    load();
    const t = setInterval(() => setNow(Date.now()), 30000);
    const r = setInterval(load, 300000);
    return () => { clearInterval(t); clearInterval(r); };
  }, [open]);
  // once the shown window has closed, pull the next one instead of sitting on "due now"
  useEffect(() => {
    if (open && s?.next_resolve_at && new Date(s.next_resolve_at) <= now) api.predictionSchedule().then(setS).catch(() => {});
  }, [now, open, s]);
  if (!s || !s.pending) return null;
  return (
    <div className="border border-atlas-cyan/30 bg-atlas-cyan/[0.04] p-3 mb-4" data-testid="scoring-clock">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-atlas-cyan">
          <Hourglass className="w-3.5 h-3.5" /> Next window closes
        </span>
        <span className="text-xl font-mono text-white" data-testid="scoring-clock-countdown">{s.next_resolve_at ? left(s.next_resolve_at, now) : "—"}</span>
      </div>
      <p className="text-[11px] text-atlas-secondary mt-1" data-testid="scoring-clock-summary">
        Hits are recorded as they happen · next miss check: {missCheck(s.next_final_at)}
      </p>
      <div className="flex flex-wrap gap-x-4 text-[10px] font-mono text-atlas-secondary mt-1">
        <span data-testid="scoring-clock-grace">In {s.grace_hours}h grace: <span className="text-amber-300">{s.in_grace}</span></span>
        <span data-testid="scoring-clock-cadence">Scoring runs every {s.cadence_min} min{s.last_run ? ` · last ${new Date(s.last_run).toISOString().slice(11, 16)} UTC` : ""}</span>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
        {s.by_hazard.map((h) => (
          <span key={h.hazard} className="text-[10px] font-mono text-atlas-secondary" data-testid={`scoring-clock-${h.hazard}`}>
            {HAZARD_LABEL[h.hazard] || h.hazard} <span className="text-white">{h.pending}</span>
            {h.next_resolve_at && <span className="text-atlas-cyan/70"> · {left(h.next_resolve_at, now)}</span>}
            {!h.confirmable && <span className="text-amber-300"> · no agency feed</span>}
          </span>
        ))}
      </div>
    </div>
  );
};
